import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, AlertTriangle } from 'lucide-react';

export const Hero = () => {
  const scrollToCourses = () => {
    const element = document.getElementById('courses');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-[#FFFBF0] to-white">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FFD700]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#B8960C]">Deine finanzielle Zukunft beginnt heute</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] bg-clip-text text-transparent">
                Finanzielle Intelligenz
              </span>
              <br />
              <span className="text-gray-900">kannst Du lernen!</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Finanzielle Bildung wurde im Lehrplan der meisten Schulen leider irgendwie „vergessen". 
              Daher ist es von enormer Bedeutung für ein gutes Leben, Dich selbst zu bilden und diese 
              Kompetenz als Erwachsener zu entwickeln.
            </p>

            {/* Warning Box */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-r-lg max-w-2xl mx-auto lg:mx-0">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-amber-900 mb-1">Achtung!</h3>
                  <p className="text-sm text-amber-800">
                    Das Seminar kann nachhaltige Folgen haben. Du wirst an der Verbesserung Deines 
                    finanziellen Fundaments arbeiten, neue Fähigkeiten entwickeln und Deinen Fokus schärfen.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xl font-bold text-gray-900 mb-8 max-w-2xl mx-auto lg:mx-0">
              Bist Du bereit für ein finanziell erfolgreicheres Leben?
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={scrollToCourses}
                size="lg"
                className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white shadow-xl hover:shadow-2xl transition-all duration-300 text-lg px-8 py-6"
              >
                Zur Terminübersicht
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => {
                  const element = document.getElementById('about');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-lg px-8 py-6"
              >
                Mehr erfahren
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-[#D4AF37]/20">
              <div>
                <div className="text-3xl font-bold text-[#D4AF37]">8+</div>
                <div className="text-sm text-gray-600 mt-1">Kurse</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#D4AF37]">25+</div>
                <div className="text-sm text-gray-600 mt-1">Jahre Erfahrung</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#D4AF37]">100%</div>
                <div className="text-sm text-gray-600 mt-1">Praxisnah</div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBncm93dGh8ZW58MHx8fHwxNzYwOTg2NDc3fDA&ixlib=rb-4.1.0&q=85"
                alt="Finanzielles Wachstum - Pflanze wächst aus Münzen"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#D4AF37]/20 to-transparent"></div>
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-[#D4AF37]/20">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Starte jetzt</div>
                  <div className="font-bold text-[#D4AF37]">Deine Reise</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
